import { TabStyles, Tooltip, TooltipPosition } from '@tableau/widgets';
import * as React from 'react';
import { classes, style } from 'typestyle';
import { Messages } from '../../gen-src/Localize';
import { SvgIcon, SvgIconName } from '../resources/SvgIcon';

export interface SnapshotBannerProps {
  isExpanded?: boolean;
  tooltipText?: string;
  isSmallCard?: boolean;
}

export const SnapshotBanner = (props: SnapshotBannerProps): JSX.Element => {
  const baseStyle = bannerBaseStyle(!!props.isSmallCard);
  const hoverStyle = bannerHoverStyle(!!props.isSmallCard);

  const bannerStyle = !!props.isExpanded ? hoverStyle : baseStyle;

  const getTooltipContent = () => <span>{props.tooltipText}</span>;

  const bannerElement = (
    <div data-tb-test-id="metric-card-snapshot-idk" className={bannerStyle}>
      <SvgIcon name={SvgIconName.CAMERA} altText="" style={imageStyle(!!props.isSmallCard)} color={'#FFFFFF'} />
      <div style={textStyle}>{Messages.Snapshot()}</div>
    </div>
  );

  // We only need to show the tooltip while the banner is expanded, but we also don't want to
  // show it when it's not expanded since the tooltip will look like it's in the incorrect
  // place then.
  const shouldIncludeTooltip = props.tooltipText !== undefined && props.isExpanded;

  return (
    <div style={anchoringContainerStyle}>
      {shouldIncludeTooltip ? (
        <Tooltip getContent={getTooltipContent} position={TooltipPosition.Bottom}>
          {bannerElement}
        </Tooltip>
      ) : (
        bannerElement
      )}
    </div>
  );
};

const BANNER_HEIGHT = 28; // 28 is the height of the recommendation card banners
const SMALL_BANNER_HEIGHT = 24; // smaller height on preview cards so it doesn't overlap BAN
const ICON_WIDTH = 14;

const bannerBaseStyle = (isSmallCard: boolean): string =>
  style({
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    background: '#4C86A9',
    zIndex: 4, // needs to appear above darken-on-hover

    left: `calc(100% - ${isSmallCard ? SMALL_BANNER_HEIGHT : BANNER_HEIGHT}px)`, // we want the exposed part to be square
    transition: 'transform .5s',
    paddingRight: '10px',
    height: isSmallCard ? `${SMALL_BANNER_HEIGHT}px` : `${BANNER_HEIGHT}px`,

    fontWeight: 500,

    $nest: {
      '&:focus, &:focus-visible': {
        boxShadow: '0 0 4px 0 #3498db',
        outline: 'none',
      },
    },
  });

const bannerHoverStyle = (isSmallCard: boolean): string =>
  classes(
    bannerBaseStyle(isSmallCard),
    style({
      transform: 'translate(calc(28px - 100%), 0)',
      transition: 'transform .5s',
    })
  );

const anchoringContainerStyle: React.CSSProperties = {
  position: 'absolute',
  width: '100%',
};

const textStyle: React.CSSProperties = {
  paddingTop: '2px',
  float: 'right',
  fontSize: TabStyles.Typography.Sizing.Small,
  color: TabStyles.Typography.Color.InvertedPrimary,
  pointerEvents: 'none',
};

const imageStyle = (isSmallCard: boolean): React.CSSProperties => {
  const bannerHeight = isSmallCard ? SMALL_BANNER_HEIGHT : BANNER_HEIGHT;

  return { margin: `0 ${Math.ceil((bannerHeight - ICON_WIDTH) / 2)}px` };
};
